import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const USER_TYPE_LABELS = {
  org_superadmin:      'Org Superadmin',
  compliance_officer:  'Compliance Officer',
  compliance_analyst:  'Compliance Analyst',
  department_lead:     'Department Lead',
  department_user:     'Department User',
};

// Friendly labels for the open-work counts returned by GET /users/:id/open-work
const WORK_LABELS = {
  compliance_issues_open:  { label: 'Open Compliance Issues', key: 'compliance_issues' },
  coa_activities_open:     { label: 'COA Activities',          key: 'coa_activities' },
  risks_owned:             { label: 'Risks Owned',             key: 'risks' },
  risk_mitigation_plans:   { label: 'Risk Mitigation Plans',   key: 'risk_mitigation_plans' },
  risk_mitigation_actions: { label: 'Risk Mitigation Actions', key: 'risk_mitigation_actions' },
  calendar_obligations:    { label: 'Calendar Obligations',    key: 'calendar_obligations' },
  assessment_assignments:  { label: 'Risk Assessment Tasks',   key: 'assessment_assignments' },
};

/**
 * ReplacePersonModal — the "killer feature" of the People page.
 *
 * Replaces a leaver with a new person in one atomic action:
 *   1. Show counts of all open work assigned to the leaver
 *   2. Pre-check the reassignment toggles
 *   3. Form fields for the new person
 *   4. Single submit → POST /api/admin/v2/users/:id/replace
 *      → creates new user, copies user_app_roles, transfers open work,
 *        deactivates leaver, sends welcome email
 */
export default function ReplacePersonModal({ person, onClose, onComplete }) {
  // Phase: 'loading' | 'form' | 'submitting' | 'error'
  const [phase, setPhase] = useState('loading');
  const [openWork, setOpenWork] = useState({});
  const [error, setError] = useState(null);

  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [copyRole, setCopyRole] = useState(true);
  const [reason, setReason] = useState('');

  // Reassignment toggles, default all true
  const [reassign, setReassign] = useState({
    compliance_issues: true,
    coa_activities: true,
    risks: true,
    risk_mitigation_plans: true,
    risk_mitigation_actions: true,
    calendar_obligations: true,
    assessment_assignments: true,
  });

  // ─── Load open-work counts on mount ───
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/v2/users/${person.id}/open-work`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setOpenWork(data.counts || {});
          setPhase('form');
        } else {
          setError(data.message || 'Failed to load open work counts');
          setPhase('error');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
        setPhase('error');
      }
    })();
  }, [person.id]);

  const totalOpenItems = Object.values(openWork).reduce((sum, n) => sum + (Number(n) || 0), 0);

  const toggleReassign = (key) => setReassign(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSubmit = async () => {
    setError(null);
    if (!newName.trim() || !newEmail.trim()) return setError('New person name and email are required');
    if (!/^\S+@\S+\.\S+$/.test(newEmail)) return setError('Invalid email format');
    if (newEmail.toLowerCase() === person.email.toLowerCase()) return setError('New email cannot match the leaver');

    setPhase('submitting');
    try {
      const res = await fetch(`${API}/api/admin/v2/users/${person.id}/replace`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_user: { name: newName, email: newEmail, send_invite: sendInvite },
          copy_role: copyRole,
          reassign,
          reason: reason || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        onComplete(data);
      } else {
        setError(data.message || 'Failed to replace person');
        setPhase('form');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
      setPhase('form');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Replace Person</h2>
            <p className="text-xs text-slate-500 mt-0.5">Atomic swap: deactivate the leaver, create their replacement, and transfer open work</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center">×</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {phase === 'loading' && (
            <div className="text-center py-12">
              <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-400 text-sm">Counting open work for {person.name}…</p>
            </div>
          )}

          {phase === 'submitting' && (
            <div className="text-center py-12">
              <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-400 text-sm">Replacing {person.name}…</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <span className="text-2xl">⚠</span>
              </div>
              <p className="text-red-300 text-sm mb-4">{error}</p>
              <button onClick={onClose} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white">Close</button>
            </div>
          )}

          {phase === 'form' && (
            <div className="space-y-5">
              {/* Leaver summary */}
              <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-4">
                <p className="text-[11px] uppercase tracking-wide text-amber-400 font-semibold mb-2">Leaving</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-semibold text-white">{person.name}</p>
                    <p className="text-xs text-slate-400">{person.email}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs bg-slate-700/50 px-2 py-1 rounded text-slate-300">
                      {USER_TYPE_LABELS[person.user_type] || person.user_type}
                    </span>
                    {person.department_name && (
                      <p className="text-[11px] text-slate-500 mt-1">{person.department_name}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* New person form */}
              <Section title="1. New Person">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Full Name *">
                    <input
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      placeholder="Their replacement"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      placeholder="newperson@org.com"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    />
                  </Field>
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer text-xs text-slate-300">
                  <input
                    type="checkbox"
                    checked={sendInvite}
                    onChange={() => setSendInvite(v => !v)}
                    className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                  />
                  Send welcome email with set-password link
                </label>
              </Section>

              {/* Permissions */}
              <Section title="2. Permissions">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={copyRole}
                    onChange={() => setCopyRole(v => !v)}
                    className="mt-0.5 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-white">Copy {person.name}'s exact role and access</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Inherits user type ({USER_TYPE_LABELS[person.user_type] || 'their type'}), department, and all per-app role grants.
                      {!copyRole && ' (When unchecked, you\'ll edit the new user from the People page after creation.)'}
                    </p>
                  </div>
                </label>
              </Section>

              {/* Open work transfer */}
              <Section title={`3. Transfer Open Work ${totalOpenItems > 0 ? `(${totalOpenItems} items found)` : '(nothing open)'}`}>
                {totalOpenItems === 0 ? (
                  <p className="text-xs text-slate-500 italic">No open work to transfer. The leaver has no in-flight items assigned.</p>
                ) : (
                  <div className="space-y-1.5">
                    {Object.entries(WORK_LABELS).map(([countKey, def]) => {
                      const count = openWork[countKey];
                      if (count === null || count === undefined || count === 0) return null;
                      return (
                        <label key={countKey} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-800/40 border border-slate-700/40 cursor-pointer hover:border-slate-600">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={reassign[def.key]}
                              onChange={() => toggleReassign(def.key)}
                              className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm text-white">Reassign {def.label}</span>
                          </div>
                          <span className="text-xs bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full">{count} open</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </Section>

              {/* Reason */}
              <Section title="4. Reason (optional)">
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Resigned 2026-04-01, replaced by Jane on 2026-04-15"
                  rows={2}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none resize-none"
                />
                <p className="text-[11px] text-slate-500 mt-1">Recorded in the audit log for compliance traceability.</p>
              </Section>

              {/* What will happen — final review */}
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                <p className="text-[11px] uppercase tracking-wide text-blue-400 font-semibold mb-2">What will happen on submit</p>
                <ul className="text-xs text-slate-300 space-y-1">
                  <li>✓ {newName || '(new person)'} created with email {newEmail || '(email)'}</li>
                  {copyRole && <li>✓ Inherits {person.name}'s exact role and per-app permissions</li>}
                  {totalOpenItems > 0 && Object.entries(reassign).some(([_, on]) => on) && (
                    <li>✓ Open work transferred to new person</li>
                  )}
                  <li>✗ {person.name} marked as <strong className="text-amber-300">replaced</strong> — can no longer log in</li>
                  <li>✓ Historical audit trail and authorship preserved</li>
                  <li>✓ Audit log entry written</li>
                  {sendInvite && <li>✓ Welcome email sent to {newEmail || '(new email)'}</li>}
                </ul>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {phase === 'form' && (
          <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between flex-shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white text-sm transition">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={!newName.trim() || !newEmail.trim()}
              className="px-5 py-2 bg-gradient-to-r from-amber-500 to-red-600 rounded-lg font-medium text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-amber-500/25 transition"
            >
              Replace {person.name}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-2">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}
